import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useFlaggedPosts, useModeratePost } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Shield, RotateCcw, Trash2, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function AdminModeration() {
  const { data: flaggedPosts, isLoading } = useFlaggedPosts();
  const moderatePost = useModeratePost();
  const { toast } = useToast();

  const handleModerate = async (postId: string, action: 'delete' | 'restore') => {
    try {
      await moderatePost.mutateAsync({ postId, action });
      toast({
        title: action === 'restore' ? 'Post restored' : 'Post deleted',
        description:
          action === 'restore'
            ? 'The post has been restored and is now visible.'
            : 'The post has been permanently hidden.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">
          Moderation
        </h1>
        <p className="text-muted-foreground text-sm">
          Review and moderate community content
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Deleted Posts</CardTitle>
          <CardDescription>
            Posts that have been soft-deleted and may need review
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !flaggedPosts || flaggedPosts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-500/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                No posts requiring moderation
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {flaggedPosts.map((post: any) => (
                <div
                  key={post.id}
                  className="border border-border rounded-lg p-4 space-y-3"
                >
                  {/* Author Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <SignedAvatarImage src={post.profiles?.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {post.profiles?.full_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-sm">
                          {post.profiles?.full_name || 'Unknown User'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(post.created_at), {
                            addSuffix: true,
                          })}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-red-100 text-red-800">
                      Deleted
                    </Badge>
                  </div>

                  {/* Post Content */}
                  <div className="bg-muted/50 rounded p-3">
                    <p className="text-sm whitespace-pre-wrap">{post.content}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleModerate(post.id, 'restore')}
                      disabled={moderatePost.isPending}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleModerate(post.id, 'delete')}
                      disabled={moderatePost.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Keep Deleted
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Moderation Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Review deleted content</strong> -
            Posts that users or moderators have deleted appear here for review.
          </p>
          <p>
            <strong className="text-foreground">Restore</strong> - Returns the
            post to its original location if it was deleted in error.
          </p>
          <p>
            <strong className="text-foreground">Keep Deleted</strong> - Confirms
            the deletion. The post remains hidden from users.
          </p>
          <div className="pt-4 border-t">
            <p className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Only admins and moderators have access to this page.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
